import { GraduationCap, Award, Briefcase, School } from "lucide-react";

interface CredentialsSectionProps {
  highestQualification?: string | null;
  highestQualificationInstitution?: string | null;
  teachingQualification?: string | null;
  yearsTeachingExperience?: string | null;
  formerSchoolTeacher?: boolean | null;
  formerInstitutionName?: string | null;
  formerInstitutionYears?: number | null;
}

const QUAL_LABELS: Record<string, string> = {
  BELOW_SECONDARY: "Below Secondary",
  SECONDARY:       "Secondary",
  DIPLOMA:         "Diploma",
  BACHELOR:        "Bachelor's Degree",
  MASTER:          "Master's Degree",
  DOCTORATE:       "PhD / Doctorate",
  OTHER:           "Other Graduate",
};

const TEACHING_QUAL_LABELS: Record<string, string> = {
  NONE: "No formal teaching qualification",
  PGCE: "PGCE", B_ED: "B.Ed", M_ED: "M.Ed", D_ED: "D.Ed",
  BTC: "BTC", NTT: "NTT", TTC: "TTC",
  CTET: "CTET Certified", STET: "STET Certified",
  OTHER: "Other teaching qualification",
};

const EXP_LABELS: Record<string, string> = {
  LESS_THAN_1:  "< 1 year",
  ONE_TO_3:     "1–3 years",
  THREE_TO_5:   "3–5 years",
  FIVE_TO_10:   "5–10 years",
  MORE_THAN_10: "10+ years",
};

export function CredentialsSection({
  highestQualification,
  highestQualificationInstitution,
  teachingQualification,
  yearsTeachingExperience,
  formerSchoolTeacher,
  formerInstitutionName,
  formerInstitutionYears,
}: CredentialsSectionProps) {
  const hasFormerSchool    = formerSchoolTeacher && formerInstitutionName;
  const qualLabel          = highestQualification ? (QUAL_LABELS[highestQualification] ?? highestQualification) : null;
  const teachingQualLabel  = teachingQualification ? (TEACHING_QUAL_LABELS[teachingQualification] ?? teachingQualification) : null;
  const expLabel           = yearsTeachingExperience ? (EXP_LABELS[yearsTeachingExperience] ?? yearsTeachingExperience) : null;

  const hasProfessional = teachingQualLabel || expLabel || hasFormerSchool;
  if (!qualLabel && !hasProfessional) return null;

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-5 w-1 rounded-full bg-gradient-to-b from-violet-500 to-purple-500" />
        <h2 className="text-xl font-bold text-foreground">Credentials</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Academic */}
        {qualLabel && (
          <div className="rounded-xl bg-slate-100 dark:bg-slate-800/50 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <GraduationCap className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Academic</span>
            </div>
            <div className="relative pl-5">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
              <div className="relative">
                <div className="absolute -left-5 top-1.5 w-2.5 h-2.5 rounded-full border-2 border-primary bg-background" />
                <p className="text-sm font-bold text-foreground leading-snug">{qualLabel}</p>
                {highestQualificationInstitution && (
                  <p className="text-xs text-muted-foreground mt-0.5">{highestQualificationInstitution}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Professional */}
        {hasProfessional && (
          <div className="space-y-3">
            {expLabel && (
              <div className="rounded-xl bg-slate-100 dark:bg-slate-800/50 p-4 flex items-center gap-4 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-amber-500 flex items-center justify-center shrink-0 shadow-sm">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground leading-none">{expLabel}</p>
                  <p className="text-xs text-muted-foreground mt-1 font-medium">Teaching Experience</p>
                </div>
              </div>
            )}

            {teachingQualLabel && teachingQualLabel !== "No formal teaching qualification" && (
              <div className="rounded-xl bg-slate-100 dark:bg-slate-800/50 p-4 flex items-center gap-4 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-sm">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground leading-snug">{teachingQualLabel}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-medium">Teaching Qualification</p>
                </div>
              </div>
            )}

            {hasFormerSchool && (
              <div className="rounded-xl bg-slate-100 dark:bg-slate-800/50 p-4 flex items-center gap-4 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center shrink-0 shadow-sm">
                  <School className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground leading-snug">{formerInstitutionName}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-medium">
                    Former School Teacher
                    {formerInstitutionYears ? ` · ${formerInstitutionYears} yr${formerInstitutionYears !== 1 ? "s" : ""}` : ""}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </section>
  );
}
